import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney, t } from '@aquabox/shared';
import { useChannels, useDrivers, useProducts, useVehicles } from '../api/queries';
import { Icon } from '../components/Icon';

/** Adımlar (§3.2). Her adımın "tamam" ölçütü SAYILABİLİR bir gerçektir, kullanıcının onayı değil. */
const STEPS = [
  {
    key: 'products',
    titleKey: 'setup.s1Title' as const,
    leadKey: 'setup.s1Lead' as const,
    noteKey: 'setup.s1Note' as const,
    goto: '/urunler',
    ctaKey: 'setup.s1Cta' as const,
  },
  {
    key: 'vehicles',
    titleKey: 'setup.s2Title' as const,
    leadKey: 'setup.s2Lead' as const,
    noteKey: 'setup.s2Note' as const,
    goto: '/araclar',
    ctaKey: 'setup.s2Cta' as const,
  },
  {
    key: 'team',
    titleKey: 'setup.s3Title' as const,
    leadKey: 'setup.s3Lead' as const,
    noteKey: 'setup.s3Note' as const,
    goto: '/personel',
    ctaKey: 'setup.s3Cta' as const,
  },
  {
    key: 'channels',
    titleKey: 'setup.s4Title' as const,
    leadKey: 'setup.s4Lead' as const,
    noteKey: 'setup.s4Note' as const,
    goto: '/entegrasyonlar',
    ctaKey: 'setup.s4Cta' as const,
  },
];

/**
 * Kurulum sihirbazı (§3.2).
 *
 * PROTOTİPTEN İKİ FARK, ikisi de bilerek:
 *
 * 1. Prototipte satırlar SABİT ve salt okunurdu ("Hayat 19L — ₺120 · depozito ₺100"). Burada
 *    satırlar GERÇEK kayıtlardan geliyor; boşsa boş görünüyor. Kurulum ekranının işi "her şey
 *    hazır" hissi vermek değil, neyin eksik olduğunu söylemek.
 * 2. Her adım kendi CRUD formunu tekrar yazmıyor, ilgili ekrana gönderiyor. Aynı formun iki
 *    kopyası er geç ayrışır — biri depozitoyu sorar, diğeri sormaz.
 *
 * "Tamamlandı" işareti kullanıcının bastığı bir düğme değil, sayının kendisi: ürün varsa adım
 * yeşil. Böylece sihirbaz yarıda bırakılıp dönüldüğünde doğru yerden devam eder.
 */
export function Setup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const products = useProducts();
  const vehicles = useVehicles();
  const drivers = useDrivers();
  const channels = useChannels();

  const productItems = products.data?.items ?? [];
  const vehicleItems = vehicles.data?.items ?? [];
  const driverItems = drivers.data?.items ?? [];
  const channelItems = channels.data ?? [];

  const done: Record<string, boolean> = {
    products: productItems.length > 0,
    vehicles: vehicleItems.length > 0,
    team: driverItems.length > 0,
    // Soğuk test fazında hiçbir bağlantı ACTIVE olamaz (§0); "tamam" ölçütü bu yüzden
    // aktiflik DEĞİL, giriş bilgisinin kaydedilmiş olması.
    channels: channelItems.some((c) => c.hasCredentials),
  };

  const current = STEPS[step]!;

  const rows: { label: string; meta: string }[] = (() => {
    switch (current.key) {
      case 'products':
        return productItems.map((p) => ({
          label: p.name,
          meta: p.returnsEmpty
            ? `${formatMoney(p.unitPrice)} · ${t('prod.deposit')} ${formatMoney(p.depositAmount)}`
            : formatMoney(p.unitPrice),
        }));
      case 'vehicles':
        return vehicleItems.map((v) => ({
          label: v.plate,
          meta: v.label ?? t('setup.noLabel'),
        }));
      case 'team':
        return driverItems.map((d) => ({
          label: d.user.fullName,
          meta: d.vehicle ? d.vehicle.plate : t('fleet.noVehicle'),
        }));
      default:
        return channelItems.map((c) => ({
          label: t(`orderSource.${c.source}` as Parameters<typeof t>[0]),
          meta: c.hasCredentials ? t('setup.chReady') : t('setup.chPending'),
        }));
    }
  })();

  return (
    <div style={{ padding: '28px 32px 44px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ maxWidth: 900, width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* ── Adım göstergesi ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`,
            position: 'relative',
            marginBottom: 28,
          }}
        >
          {/* Arkadaki çizgi: ilk ve son dairenin ortasından geçsin diye kenarlardan içeri alınır. */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: '12.5%',
              right: '12.5%',
              top: 15,
              height: 2,
              background: 'var(--border)',
            }}
          />
          {STEPS.map((s, i) => {
            const complete = done[s.key] === true;
            const active = i === step;
            return (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                style={{
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: 0,
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                    fontSize: 13,
                    fontVariantNumeric: 'tabular-nums',
                    background: complete
                      ? 'var(--success)'
                      : active
                        ? 'var(--primary)'
                        : 'var(--surface)',
                    color: complete || active ? '#fff' : 'var(--text-secondary)',
                    border: complete || active ? 'none' : '2px solid var(--border)',
                  }}
                >
                  {complete ? '✓' : i + 1}
                </span>
                <span
                  style={{
                    fontWeight: active ? 600 : 500,
                    fontSize: 13,
                    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  }}
                >
                  {t(`setup.step${i + 1}` as Parameters<typeof t>[0])}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Adım kartı ── */}
        <div
          className="card"
          style={{ padding: '32px 34px', display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <h2 className="h1" style={{ margin: 0 }}>
            {t(current.titleKey)}
          </h2>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
              maxWidth: 560,
              margin: 0,
            }}
          >
            {t(current.leadKey)}
          </p>

          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-box)',
              padding: '4px 16px',
            }}
          >
            {rows.length === 0 ? (
              <p
                style={{
                  fontSize: 13.5,
                  color: 'var(--text-secondary)',
                  padding: '18px 0',
                  margin: 0,
                }}
              >
                {t('setup.emptyStep')}
              </p>
            ) : (
              rows.map((row, i) => (
                <div
                  key={`${row.label}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    minHeight: 56,
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'var(--success)',
                      color: '#fff',
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>{row.label}</span>
                  <span
                    style={{
                      fontSize: 14,
                      color: 'var(--text-secondary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {row.meta}
                  </span>
                </div>
              ))
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
            <p
              style={{
                fontSize: 13.5,
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
                maxWidth: 420,
                margin: 0,
                flex: 1,
              }}
            >
              {t(current.noteKey)}
            </p>
            <button className="btn" onClick={() => void navigate(current.goto)}>
              <Icon name="arrow-right" size={15} /> {t(current.ctaKey)}
            </button>
            {step > 0 ? (
              <button className="btn" onClick={() => setStep(step - 1)}>
                {t('setup.back')}
              </button>
            ) : null}
            {step < STEPS.length - 1 ? (
              <button className="btn btn-primary" onClick={() => setStep(step + 1)}>
                {t('setup.next')}
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => void navigate('/entegrasyonlar')}>
                {t('setup.finish')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
